import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as dealModeSelectors from '@makemydeal/dr-dash-store/selectors/dealModeSelectors';
import * as dealScreenErrorSelectors from '@makemydeal/dr-dash-store/selectors/dealScreenErrorSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import { trackValidationErrorOnExit } from '@makemydeal/dr-dash-store/utils/usabilityTelemetry';
import { useValidationGuardContext } from '../modals/ValidationGuardProvider';
import { validateSaveState } from '@makemydeal/dr-dash-store/actions/saveValidationActionCreators';
import type { SaveValidationErrorKeys } from '@makemydeal/dr-dash-store/utils/saveValidationRunner';
import * as paymentSelectors from '@makemydeal/dr-dash-store/selectors/payment';

type WrapFn = <TArgs extends unknown[]>(fn: (...args: TArgs) => void) => (...args: TArgs) => void;

type ValidationGuard = {
    wrap: WrapFn;
};

export type UseValidationGuardOptions = {
    skipValidationKeys?: SaveValidationErrorKeys[];
    skipGuardValidation?: boolean;
};

export const useValidationGuard = (type: 'exit' | 'save' = 'exit', options?: UseValidationGuardOptions): ValidationGuard => {
    const dispatch = useDispatch();
    const { openExitModal, openSaveModal } = useValidationGuardContext();
    const openModal = type === 'save' ? openSaveModal : openExitModal;
    const useOptionalDeskingFields = useSelector(dealModeSelectors.isOptionalDeskingValidationEnabled);
    const skipGuardValidation = options?.skipGuardValidation === true;
    const entitiesWithErrors = useSelector(dealScreenErrorSelectors.getEntitiesWithErrorCounts);
    const isPaymentError = useSelector(paymentSelectors.getIsError);
    const skipValidationKeys = options?.skipValidationKeys;

    // Gather context attributes for New Relic events
    const sessionId = useSelector(offerReduxSelectors.getSessionId);
    const traceId = useSelector(offerReduxSelectors.getTraceId);
    const dealXgId = useSelector(offerReduxSelectors.getDealXgId);
    const dealType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const dealerId = useSelector(dealerSelectors.getDealerId);
    const vin = useSelector(vehicleSelectors.getVehicleVin);

    const wrap = useCallback(
        <TArgs extends unknown[]>(fn: (...args: TArgs) => void) =>
            (...args: TArgs): void => {
                if (useOptionalDeskingFields && !skipGuardValidation) {
                    const { errors, hasErrors } = dispatch(validateSaveState({ skipValidationKeys }));
                    if (hasErrors || isPaymentError) {
                        trackValidationErrorOnExit(
                            {
                                guardType: type,
                                entitiesWithErrors,
                                errors
                            },
                            { sessionId, traceId, dealXgId, dealType, mmdDealerId: dealerId, vin }
                        );
                        openModal();
                        return;
                    }
                }
                fn(...args);
            },
        [
            useOptionalDeskingFields,
            skipGuardValidation,
            dispatch,
            openModal,
            type,
            isPaymentError,
            entitiesWithErrors,
            sessionId,
            traceId,
            dealXgId,
            dealType,
            dealerId,
            vin,
            skipValidationKeys
        ]
    ) as WrapFn;

    return useMemo(() => ({ wrap }), [wrap]);
};
